import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { rankSuggestion } from '../options';

type SearchSelectProps = {
  id: string;
  value: string;
  options: readonly string[];
  placeholder?: string;
  maxLength?: number;
  onChange: (value: string) => void;
};

export default function SearchSelect({
  id,
  value,
  options,
  placeholder,
  maxLength,
  onChange,
}: SearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listId = `${id}-options`;

  const matches = useMemo(() => {
    const query = value.trim();
    if (!query) return options.slice(0, 12);
    return options
      .map((option) => ({ option, score: rankSuggestion(option, query) }))
      .filter((item) => item.score >= 0)
      .sort((a, b) => b.score - a.score || a.option.localeCompare(b.option, 'zh-Hans-CN'))
      .slice(0, 12)
      .map((item) => item.option);
  }, [options, value]);

  useEffect(() => {
    if (active >= matches.length) setActive(-1);
  }, [matches.length, active]);

  const choose = (option: string) => {
    onChange(option);
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActive((current) => (matches.length ? (current + 1) % matches.length : -1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setActive((current) => (matches.length ? (current - 1 + matches.length) % matches.length : -1));
    } else if (event.key === 'Enter') {
      if (open && active >= 0 && matches[active]) {
        event.preventDefault();
        choose(matches[active]);
      }
    } else if (event.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <div
      className="resume-combo"
      ref={wrapRef}
      onBlur={(event) => {
        if (!wrapRef.current?.contains(event.relatedTarget as Node)) {
          setOpen(false);
          setActive(-1);
        }
      }}
    >
      <input
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && active >= 0 ? `${id}-option-${active}` : undefined}
        autoComplete="off"
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      <button
        type="button"
        className="resume-combo-toggle"
        tabIndex={-1}
        aria-label="展开候选"
        aria-expanded={open}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => setOpen((current) => !current)}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
          <path
            d="M2 4.5 6 8.5 10 4.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open ? (
        <ul
          className="resume-combo-list"
          id={listId}
          role="listbox"
          onMouseDown={(event) => event.preventDefault()}
        >
          {matches.length ? (
            matches.map((option, index) => (
              <li
                key={option}
                id={`${id}-option-${index}`}
                role="option"
                aria-selected={index === active}
                className={`resume-combo-option${index === active ? ' is-active' : ''}`}
                onMouseEnter={() => setActive(index)}
                onClick={() => choose(option)}
              >
                {option}
              </li>
            ))
          ) : (
            <li className="resume-combo-empty">没有匹配的选项，可直接使用当前输入。</li>
          )}
        </ul>
      ) : null}
    </div>
  );
}
